import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';

interface ConversionMetrics {
  conversion_rate: number;
  opportunity_calls: number;
  positive_calls: number;
}

interface CompanyFilter {
  id: string;
  name: string;
  enabled: boolean;
}

export function CallConversionGauges() {
  const { user } = useAuth();
  const [todayMetrics, setTodayMetrics] = useState<ConversionMetrics | null>(null);
  const [mtdMetrics, setMtdMetrics] = useState<ConversionMetrics | null>(null);
  const [personalTodayMetrics, setPersonalTodayMetrics] = useState<ConversionMetrics | null>(null);
  const [personalMtdMetrics, setPersonalMtdMetrics] = useState<ConversionMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [agentId, setAgentId] = useState<string | null>(null);
  const [companyFilters, setCompanyFilters] = useState<CompanyFilter[]>([
    { id: 'climateControl', name: 'Climate Control', enabled: true },
    { id: 'hales', name: 'Hales', enabled: true },
    { id: 'abrahams', name: 'Abrahams', enabled: true }
  ]);

  // Get agent ID for personal stats
  useEffect(() => {
    const loadAgentId = async () => {
      if (user) {
        const { data: agent } = await supabase
          .from('call_center_agents')
          .select('id')
          .eq('user_id', user.id)
          .single();
        
        if (agent) {
          setAgentId(agent.id);
        }
      }
    };
    loadAgentId();
  }, [user]);

  useEffect(() => {
    loadMetrics();
    const interval = setInterval(loadMetrics, 60000);
    return () => clearInterval(interval);
  }, [companyFilters, agentId]);

  const loadMetrics = async () => {
    try {
      const enabledCompanies = companyFilters.filter(c => c.enabled);
      const companyNames = enabledCompanies.map(c => c.name);
      
      const todayStart = new Date();
      todayStart.setHours(0, 0, 0, 0);
      
      const mtdStart = new Date();
      mtdStart.setDate(1);
      mtdStart.setHours(0, 0, 0, 0);

      // Company-wide metrics
      let todayQuery = supabase
        .from('calls')
        .select('*, call_outcomes(*)')
        .eq('direction', 'inbound')
        .gte('started_at', todayStart.toISOString());

      if (!companyFilters.find(c => c.id === 'climateControl')?.enabled) {
        todayQuery = todayQuery.in('company_name', companyNames);
      }

      const { data: todayData } = await todayQuery;

      let mtdQuery = supabase
        .from('calls')
        .select('*, call_outcomes(*)')
        .eq('direction', 'inbound')
        .gte('started_at', mtdStart.toISOString());

      if (!companyFilters.find(c => c.id === 'climateControl')?.enabled) {
        mtdQuery = mtdQuery.in('company_name', companyNames);
      }

      const { data: mtdData } = await mtdQuery;

      // Calculate company metrics
      if (todayData) {
        const opportunityCalls = todayData.filter(c => 
          c.call_outcomes?.[0]?.was_opportunity
        ).length;
        const positiveCalls = todayData.filter(c => 
          c.call_outcomes?.[0]?.outcome_type === 'opportunity_booked'
        ).length;
        const conversionRate = opportunityCalls > 0 ? (positiveCalls / opportunityCalls) * 100 : 0;
        
        setTodayMetrics({
          conversion_rate: conversionRate,
          opportunity_calls: opportunityCalls,
          positive_calls: positiveCalls
        });
      }

      if (mtdData) {
        const opportunityCalls = mtdData.filter(c => 
          c.call_outcomes?.[0]?.was_opportunity
        ).length;
        const positiveCalls = mtdData.filter(c => 
          c.call_outcomes?.[0]?.outcome_type === 'opportunity_booked'
        ).length;
        const conversionRate = opportunityCalls > 0 ? (positiveCalls / opportunityCalls) * 100 : 0;
        
        setMtdMetrics({
          conversion_rate: conversionRate,
          opportunity_calls: opportunityCalls,
          positive_calls: positiveCalls
        });
      }

      // Personal metrics (filtered by agent_id AND company filters)
      if (agentId) {
        let personalTodayQuery = supabase
          .from('calls')
          .select('*, call_outcomes(*)')
          .eq('direction', 'inbound')
          .eq('agent_id', agentId)
          .gte('started_at', todayStart.toISOString());

        if (!companyFilters.find(c => c.id === 'climateControl')?.enabled) {
          personalTodayQuery = personalTodayQuery.in('company_name', companyNames);
        }

        const { data: personalTodayData } = await personalTodayQuery;

        let personalMtdQuery = supabase
          .from('calls')
          .select('*, call_outcomes(*)')
          .eq('direction', 'inbound')
          .eq('agent_id', agentId)
          .gte('started_at', mtdStart.toISOString());

        if (!companyFilters.find(c => c.id === 'climateControl')?.enabled) {
          personalMtdQuery = personalMtdQuery.in('company_name', companyNames);
        }

        const { data: personalMtdData } = await personalMtdQuery;

        if (personalTodayData) {
          const opportunityCalls = personalTodayData.filter(c => 
            c.call_outcomes?.[0]?.was_opportunity
          ).length;
          const positiveCalls = personalTodayData.filter(c => 
            c.call_outcomes?.[0]?.outcome_type === 'opportunity_booked'
          ).length;
          const conversionRate = opportunityCalls > 0 ? (positiveCalls / opportunityCalls) * 100 : 0;
          
          setPersonalTodayMetrics({
            conversion_rate: conversionRate,
            opportunity_calls: opportunityCalls,
            positive_calls: positiveCalls
          });
        }

        if (personalMtdData) {
          const opportunityCalls = personalMtdData.filter(c => 
            c.call_outcomes?.[0]?.was_opportunity
          ).length;
          const positiveCalls = personalMtdData.filter(c => 
            c.call_outcomes?.[0]?.outcome_type === 'opportunity_booked'
          ).length;
          const conversionRate = opportunityCalls > 0 ? (positiveCalls / opportunityCalls) * 100 : 0;
          
          setPersonalMtdMetrics({
            conversion_rate: conversionRate,
            opportunity_calls: opportunityCalls,
            positive_calls: positiveCalls
          });
        }
      }
    } catch (error) {
      console.error('Error loading conversion metrics:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleCompany = (id: string) => {
    setCompanyFilters(prev => 
      prev.map(c => 
        c.id === id ? { ...c, enabled: !c.enabled } : c
      )
    );
  };

  const getCompanyTrendArrow = () => {
    if (!todayMetrics || !mtdMetrics) return null;
    
    const diff = todayMetrics.conversion_rate - mtdMetrics.conversion_rate;
    
    if (diff > 2) {
      return (
        <div className="flex flex-col items-center">
          <TrendingUp className="w-4 h-4 text-green-500" />
          <span className="text-[10px] font-semibold text-green-600">+{diff.toFixed(1)}%</span>
        </div>
      );
    } else if (diff < -2) {
      return (
        <div className="flex flex-col items-center">
          <TrendingDown className="w-4 h-4 text-red-500" />
          <span className="text-[10px] font-semibold text-red-600">{diff.toFixed(1)}%</span>
        </div>
      );
    } else {
      return (
        <div className="flex flex-col items-center">
          <Minus className="w-4 h-4 text-yellow-500" />
          <span className="text-[10px] font-semibold text-yellow-600">{diff >= 0 ? '+' : ''}{diff.toFixed(1)}%</span>
        </div>
      );
    }
  };

  const getPersonalTrendArrow = () => {
    if (!personalTodayMetrics || !personalMtdMetrics) return null;
    
    const diff = personalTodayMetrics.conversion_rate - personalMtdMetrics.conversion_rate;
    
    if (diff > 2) {
      return (
        <div className="flex flex-col items-center">
          <TrendingUp className="w-4 h-4 text-green-500" />
          <span className="text-[10px] font-semibold text-green-600">+{diff.toFixed(1)}%</span>
        </div>
      );
    } else if (diff < -2) {
      return (
        <div className="flex flex-col items-center">
          <TrendingDown className="w-4 h-4 text-red-500" />
          <span className="text-[10px] font-semibold text-red-600">{diff.toFixed(1)}%</span>
        </div>
      );
    } else {
      return (
        <div className="flex flex-col items-center">
          <Minus className="w-4 h-4 text-yellow-500" />
          <span className="text-[10px] font-semibold text-yellow-600">{diff >= 0 ? '+' : ''}{diff.toFixed(1)}%</span>
        </div>
      );
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-3">
        <div className="text-xs font-semibold text-gray-600">Call Conversion</div>
        <div className="w-16 h-16 rounded-full bg-gray-200 animate-pulse" />
        <div className="w-16 h-16 rounded-full bg-gray-200 animate-pulse" />
      </div>
    );
  }

  return (
    <div className="flex items-center gap-4">
      <div className="flex flex-col gap-1.5">
        <div className="text-xs font-bold text-gray-700">Call Conversion</div>
        <div className="flex flex-col gap-0.5">
          {companyFilters.map(company => (
            <button
              key={company.id}
              onClick={() => toggleCompany(company.id)}
              className={`text-[10px] px-1.5 py-0.5 rounded transition-all ${
                company.enabled
                  ? 'bg-blue-500 text-white font-semibold'
                  : 'bg-gray-200 text-gray-600'
              }`}
            >
              {company.name}
            </button>
          ))}
        </div>
      </div>

      <div className="flex items-center gap-3">
        <div className="flex flex-col items-center gap-1">
          <div className="text-[10px] font-bold text-gray-600 mb-0.5">Company</div>
          <div className="flex items-center gap-2">
            <NeedleGauge
              value={todayMetrics?.conversion_rate || 0}
              label="Today"
              subtitle={`${todayMetrics?.positive_calls || 0}/${todayMetrics?.opportunity_calls || 0}`}
            />
            <NeedleGauge
              value={mtdMetrics?.conversion_rate || 0}
              label="MTD"
              subtitle={`${mtdMetrics?.positive_calls || 0}/${mtdMetrics?.opportunity_calls || 0}`}
            />
            <div className="flex flex-col items-center justify-end min-w-[30px] h-full">
              <div className="flex-1 flex items-center justify-center">
                {getCompanyTrendArrow()}
              </div>
              <div className="text-[10px] font-semibold text-gray-700">Trend</div>
            </div>
          </div>
        </div>

        <div className="w-px h-16 bg-gray-300" />

        <div className="flex flex-col items-center gap-1">
          <div className="text-[10px] font-bold text-gray-600 mb-0.5">Personal</div>
          <div className="flex items-center gap-2">
            <NeedleGauge
              value={personalTodayMetrics?.conversion_rate || 0}
              label="Today"
              subtitle={`${personalTodayMetrics?.positive_calls || 0}/${personalTodayMetrics?.opportunity_calls || 0}`}
            />
            <NeedleGauge
              value={personalMtdMetrics?.conversion_rate || 0}
              label="MTD"
              subtitle={`${personalMtdMetrics?.positive_calls || 0}/${personalMtdMetrics?.opportunity_calls || 0}`}
            />
            <div className="flex flex-col items-center justify-end min-w-[30px] h-full">
              <div className="flex-1 flex items-center justify-center">
                {getPersonalTrendArrow()}
              </div>
              <div className="text-[10px] font-semibold text-gray-700">Trend</div>
            </div>
          </div>
        </div>

        <div className="w-px h-16 bg-gray-300" />
      </div>
    </div>
  );
}

interface NeedleGaugeProps {
  value: number;
  label: string;
  subtitle: string;
}

function NeedleGauge({ value, label, subtitle }: NeedleGaugeProps) {
  const getGaugeColor = (rate: number): string => {
    if (rate >= 85) return '#22c55e';
    if (rate >= 76) return '#eab308';
    return '#ef4444';
  };

  const color = getGaugeColor(value);
  const clampedValue = Math.min(Math.max(value, 0), 100);
  const needleRotation = -90 + (clampedValue * 1.8);

  return (
    <div className="flex flex-col items-center">
      <div className="relative w-16 h-8">
        <svg className="absolute inset-0 w-full h-full" viewBox="0 0 120 60">
          <path
            d="M 10 55 A 50 50 0 0 1 45 10"
            fill="none"
            stroke="#ef4444"
            strokeWidth="10"
            strokeLinecap="round"
          />
          <path
            d="M 45 10 A 50 50 0 0 1 75 10"
            fill="none"
            stroke="#eab308"
            strokeWidth="10"
            strokeLinecap="round"
          />
          <path
            d="M 75 10 A 50 50 0 0 1 110 55"
            fill="none"
            stroke="#22c55e"
            strokeWidth="10"
            strokeLinecap="round"
          />
          
          <g transform="translate(60, 55)">
            <line
              x1="0"
              y1="0"
              x2="0"
              y2="-38"
              stroke="#1f2937"
              strokeWidth="2.5"
              strokeLinecap="round"
              transform={`rotate(${needleRotation})`}
            />
            <circle cx="0" cy="0" r="4" fill="#1f2937" />
          </g>
        </svg>
      </div>
      
      <div className="text-center">
        <div className="text-base font-bold leading-none" style={{ color }}>
          {clampedValue.toFixed(1)}%
        </div>
        <div className="text-[10px] font-semibold text-gray-700">
          {label}
        </div>
        <div className="text-[9px] text-gray-600">
          {subtitle}
        </div>
      </div>
    </div>
  );
}
